import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getIngestionBulkApproveUrl,
    getIngestionDraftBulkUpdateUrl,
    getIngestionDraftByIdUrl,
    getIngestionDraftsUrl,
    getTagUrl,
} from '../../utils/UrlUtilities';
import { constructIngestionBulkApprovePayload, constructIngestionDraftBulkUpdatePayload } from '../../utils/PayloadUtils';
import {
    INGESTION_DRAFT_STATUSES,
    QUESTIONDIFFICULTY,
    TAGLEVELS,
    DIVISION_VALUES,
    UNIT_VALUES,
    isDivisionLevel,
    isUnitLevel,
    itemsPerPageOptions,
} from '../../utils/Constants';
import { getMissingApprovalFields } from '../../utils/DraftValidation';
import MathText from '../ui/MathText';
import ParsedDraftEditor from './ParsedDraftEditor';
import BulkEditModal from './BulkEditModal';

const STATUS_VARIANT = {
    PENDING_REVIEW: 'bg-warning text-dark',
    APPROVED: 'bg-success',
    REJECTED: 'bg-secondary',
};

const truncate = (s, n = 80) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s);

// "All" page-size sentinel — a limit large enough to return the whole filtered set in one page
// (the backend raises its cap to match). Drafts are admin-scale, so this comfortably covers them.
const ALL_PAGE_SIZE = 100000;

// Last path segment of a source file/folder path ("/a/b/MT-01.pdf" → "MT-01.pdf").
const baseName = (p) => {
    const parts = String(p || '').split(/[/\\]/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
};

// Number of GRE Text-Completion blanks on a draft (answers live in metadata.blanks).
const blankCount = (d) => ((d.metadata && d.metadata.blanks) || []).length;

// Text Completion answer key: metadata.blanks = [{choices:[...], correct:<0-based index>}, ...].
// Answered = every blank has a picked correct choice.
const blanksAllAnswered = (d) => {
    const bl = (d.metadata && d.metadata.blanks) || [];
    return bl.length > 0 && bl.every(b => b && b.correct != null && b.correct !== '');
};
// Compact per-blank answer label, e.g. "A·C·B" (choice index → letter, one per blank).
const blanksAnswerLabel = (d) =>
    ((d.metadata && d.metadata.blanks) || []).map(b => 'ABCDEFGHIJ'[Number(b.correct)] || '?').join('·');

// Drafts table with filters, pagination, multi-select + bulk approve + bulk reject,
// and a row-click that opens the split-view editor.
//
// Props:
//   jobIdFilter         — when set, scope to a single ingestion job.
//   onClearJobFilter    — callback to remove the job scope (X on the chip).
//   onApproved          — bubbled up to IngestionPage's "last approved" ticker.
//   reviewMode          — the "All Yet To Review" tab: always scope to PENDING_REVIEW
//                         (no status dropdown) and expose job-id + initiator-email
//                         filters over the global pending pile.
const ParsedDraftsGrid = ({ jobIdFilter, onClearJobFilter, onApproved, reviewMode = false }) => {
    const CallApi = useApiCaller();
    // useApiCaller returns a fresh function each render. Pin the latest via a ref
    // so fetchDrafts doesn't need it in deps — otherwise the useEffect below
    // re-fires every render and hammers the API. Same pattern as IngestionJobList.
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    const [drafts, setDrafts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    // Default to "All" so the whole filtered draft set shows on one page.
    const [pageSize, setPageSize] = useState(ALL_PAGE_SIZE);
    // Free-text search across question text / options / explanation, over the WHOLE filtered set
    // (all pages) — server-side. `searchInput` is what the user types; `search` is the debounced
    // value actually sent.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    // Default to "All statuses" so a batch's drafts stay listed with their live status after being
    // approved/rejected (a PENDING_REVIEW default would drop actioned rows on the next fetch, e.g.
    // when paginating). The dropdown still lets the admin narrow to a single status.
    const [statusFilter, setStatusFilter] = useState('');
    const [levelFilter, setLevelFilter] = useState('');
    const [divisionFilter, setDivisionFilter] = useState('');
    const [unitFilter, setUnitFilter] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [topicFilter, setTopicFilter] = useState('');
    const [difficultyFilter, setDifficultyFilter] = useState('');
    // Review-mode-only filters (the "All Yet To Review" tab): scope the global
    // pending pile by job id and/or the job initiator's email.
    const [jobIdInput, setJobIdInput] = useState('');
    const [initiatorFilter, setInitiatorFilter] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [editing, setEditing] = useState(null);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    // Right-column source-document viewer (scratch PDF/image). Held here so it persists across
    // the editor remounting on Store & prev/next (the editor is keyed by draft_id).
    const [sourceDoc, setSourceDoc] = useState(null);
    // Preview modal open-state, lifted here for the same reason — so it stays open while the
    // editor remounts as the admin navigates Next/Prev inside the preview.
    const [previewOpen, setPreviewOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    // Tag taxonomy (fetched once) → level-scoped Subject/Topic filter dropdowns.
    const [tags, setTags] = useState([]);

    useEffect(() => {
        (async () => {
            const res = await callApiRef.current({ url: getTagUrl(), method: 'GET', token: true });
            setTags(res.success ? (res.data.tags || []) : []);
        })();
    }, []);

    // Subjects for the chosen level; topics for the chosen level, narrowed by subject when set
    // (IELTS topics carry their subject). Falls back to all-of-type when no level is picked.
    const subjectOptions = tags
        .filter(t => t.tag_type === 'SUBJECT' && (!levelFilter || t.level === levelFilter))
        .map(t => t.name);
    const topicOptions = tags
        .filter(t => t.tag_type === 'TOPIC' && (!levelFilter || t.level === levelFilter)
            && (!subjectFilter || t.subject === subjectFilter))
        .map(t => t.name);

    // Debounce the search box so we don't refetch on every keystroke; reset to page 1 on change.
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchDrafts = useCallback(async () => {
        const filters = { page, limit: pageSize };
        const effectiveJobId = reviewMode ? jobIdInput.trim() : jobIdFilter;
        if (effectiveJobId) filters.job_id = effectiveJobId;
        // "All Yet To Review" always scopes to the pending pile; other tabs use the dropdown.
        const effectiveStatus = reviewMode ? 'PENDING_REVIEW' : statusFilter;
        if (effectiveStatus) filters.status = effectiveStatus;
        if (levelFilter) filters.level = levelFilter;
        if (divisionFilter) filters.division = divisionFilter;
        if (unitFilter) filters.unit = unitFilter;
        if (subjectFilter) filters.subject = subjectFilter;
        if (topicFilter) filters.topic = topicFilter;
        if (difficultyFilter) filters.difficulty = difficultyFilter;
        if (search) filters.search = search;
        if (reviewMode && initiatorFilter.trim()) filters.created_by_email = initiatorFilter.trim();
        const res = await callApiRef.current({ url: getIngestionDraftsUrl(filters), method: 'GET', token: true });
        if (res.success) {
            setDrafts(res.data.drafts || []);
            setTotal(res.data.total || 0);
        }
    }, [
        page, pageSize, jobIdFilter, statusFilter, levelFilter,
        divisionFilter, unitFilter,
        subjectFilter, topicFilter, difficultyFilter, search,
        reviewMode, jobIdInput, initiatorFilter,
    ]);

    useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

    // Reset selection whenever filters or page change.
    useEffect(() => { setSelectedIds(new Set()); }, [
        page, statusFilter, levelFilter, subjectFilter,
        topicFilter, difficultyFilter, jobIdFilter,
        jobIdInput, initiatorFilter,
    ]);

    const toggleOne = (id) => {
        const next = new Set(selectedIds);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedIds(next);
    };

    const toggleAll = () => {
        if (selectedIds.size === drafts.length && drafts.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(drafts.map(d => d.draft_id)));
        }
    };

    // Per-render breakdown of which selected drafts are missing required fields.
    // Used to gate Bulk Approve (button disabled + preflight) and to surface a
    // clear per-draft message before any POST goes out.
    const selectedDraftsWithMissing = drafts
        .filter(d => selectedIds.has(d.draft_id))
        .map(d => ({ draft: d, missing: getMissingApprovalFields(d) }))
        .filter(x => x.missing.length > 0);
    const draftSerialOnPage = new Map(
        drafts.map((d, i) => [d.draft_id, (page - 1) * pageSize + i + 1])
    );

    const handleBulkApprove = async () => {
        if (selectedIds.size === 0) return;

        // Warn (but don't block) if any selected draft is incomplete — the admin can proceed or
        // cancel. On proceed, the backend still validates each one, so incomplete drafts come back
        // in `failed` while the complete ones are approved.
        if (selectedDraftsWithMissing.length > 0) {
            const lines = selectedDraftsWithMissing.map(({ draft, missing }) => {
                const serial = draftSerialOnPage.get(draft.draft_id);
                const tag = serial != null ? `#${draft.draft_id} (Serial ${serial})` : `#${draft.draft_id}`;
                return `${tag}: ${missing.join(', ')}`;
            });
            const proceed = window.confirm(
                `⚠ ${selectedDraftsWithMissing.length} of ${selectedIds.size} selected draft(s) have missing required fields:\n\n`
                + lines.join('\n')
                + '\n\nThese will be rejected by the server and reported as failed; the complete ones will still be approved.'
                + '\n\nProceed with bulk approve anyway?'
            );
            if (!proceed) return;
        } else if (!window.confirm(`Approve ${selectedIds.size} draft(s)? Each will be copied into the live question bank.`)) {
            return;
        }

        setBusy(true);
        const res = await CallApi({
            url: getIngestionBulkApproveUrl(),
            method: 'POST',
            payload: constructIngestionBulkApprovePayload([...selectedIds]),
            token: true,
        });
        setBusy(false);
        if (res.success) {
            const { approved = [], failed = [] } = res.data;
            const msg = `Approved ${approved.length}.${failed.length ? ` ${failed.length} failed — see details below.` : ''}`;
            if (failed.length > 0) {
                console.warn('Bulk approve failures:', failed);
                window.alert(msg + '\n\n' + failed.map(f => `#${f.draft_id}: ${f.reason}`).join('\n'));
            } else {
                window.alert(msg);
            }
            // Mark the succeeded drafts APPROVED in place (attempted minus failed) so the rows stay
            // visible with their new status instead of vanishing under the PENDING_REVIEW filter.
            const failedIds = new Set(failed.map(f => f.draft_id));
            const okIds = new Set([...selectedIds].filter(id => !failedIds.has(id)));
            setDrafts(prev => prev.map(d => okIds.has(d.draft_id) ? { ...d, status: 'APPROVED' } : d));
            setSelectedIds(new Set());
        }
    };

    const handleBulkReject = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Reject ${selectedIds.size} draft(s)?`)) return;
        setBusy(true);
        // DELETE per draft — no batch endpoint, and the count is small per page. DELETE soft-sets
        // status=REJECTED server-side; rejected drafts are hidden from this grid, so drop them locally.
        const rejected = new Set();
        for (const id of selectedIds) {
            const r = await CallApi({ url: getIngestionDraftByIdUrl(id), method: 'DELETE', token: true });
            if (r.success) rejected.add(id);
        }
        setBusy(false);
        setDrafts(prev => prev.filter(d => !rejected.has(d.draft_id)));
        setTotal(prev => Math.max(0, prev - rejected.size));
        setSelectedIds(new Set());
    };

    // Apply a partial field set to all selected drafts via the bulk-update endpoint.
    // Returns true on success so the modal can close itself.
    const handleBulkEdit = async (fields) => {
        if (selectedIds.size === 0) return false;
        const ids = [...selectedIds];
        setBusy(true);
        const res = await CallApi({
            url: getIngestionDraftBulkUpdateUrl(),
            method: 'POST',
            payload: constructIngestionDraftBulkUpdatePayload(ids, fields),
            token: true,
        });
        setBusy(false);
        if (res.success) {
            await fetchDrafts();
            setSelectedIds(new Set());
            return true;
        }
        return false;
    };

    // draft_ids that duplicate another loaded draft within the same job (same Q# + options).
    // Both members of a colliding group are flagged. Scoped to the loaded set (page size is "All").
    const duplicateIds = useMemo(() => {
        const groups = {};
        drafts.forEach((d) => {
            const optsSig = ['option_a', 'option_b', 'option_c', 'option_d', 'option_e', 'option_f', 'option_g', 'option_h']
                .map((k) => (d[k] || '').trim())
                .filter(Boolean)
                .join('§');
            const num = String(d.metadata?.source_number ?? '').trim();
            if (!num && !optsSig) return; // nothing meaningful to match on
            const key = `${d.job_id}|${num}|${optsSig}`;
            (groups[key] || (groups[key] = [])).push(d.draft_id);
        });
        const dup = new Set();
        Object.values(groups).forEach((arr) => { if (arr.length > 1) arr.forEach(id => dup.add(id)); });
        return dup;
    }, [drafts]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const pageBaseSerial = (page - 1) * pageSize;

    // In-page navigation for the editor's "Store & show next/previous" (natural order = the rows
    // as displayed = the drafts in this job/filter). After a store the row keeps its position
    // (status flips in place), so index-based navigation stays stable.
    const editingIndex = editing ? drafts.findIndex(d => d.draft_id === editing.draft_id) : -1;
    const navigateEditing = (direction) => {
        if (editingIndex < 0) return;
        setEditing(drafts[editingIndex + direction] || null);
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <h5 className="card-title mb-0 me-auto">Parsed Drafts {jobIdFilter && (
                        <span className="badge bg-info ms-2" title={`job #${jobIdFilter}`}>
                            {drafts[0]?.created_by_email || `job #${jobIdFilter}`}
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.6rem' }}
                                onClick={onClearJobFilter}
                            />
                        </span>
                    )}</h5>

                    {!reviewMode && (
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            title="Filter by draft status"
                        >
                            <option value="">All statuses</option>
                            {/* REJECTED is intentionally omitted — rejected drafts are hidden from this grid. */}
                            {INGESTION_DRAFT_STATUSES.filter(s => s !== 'REJECTED').map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    )}
                    {reviewMode && (
                        <>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ width: '110px' }}
                                placeholder="Job id…"
                                value={jobIdInput}
                                onChange={e => { setJobIdInput(e.target.value.replace(/[^0-9]/g, '')); setPage(1); }}
                            />
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ width: '200px' }}
                                placeholder="Initiator email contains…"
                                value={initiatorFilter}
                                onChange={e => { setInitiatorFilter(e.target.value); setPage(1); }}
                            />
                        </>
                    )}
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={levelFilter}
                        onChange={e => { setLevelFilter(e.target.value); setDivisionFilter(''); setUnitFilter(''); setSubjectFilter(''); setTopicFilter(''); setPage(1); }}
                    >
                        <option value="">All levels</option>
                        {TAGLEVELS.map(item => item.submenu
                            ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup>
                            : <option key={item.value} value={item.value}>{item.label}</option>
                        )}
                    </select>
                    {isDivisionLevel(levelFilter) && (
                        <select className="form-select form-select-sm" style={{ width: 'auto' }} value={divisionFilter} onChange={e => { setDivisionFilter(e.target.value); setPage(1); }}>
                            <option value="">All divisions</option>
                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    )}
                    {isUnitLevel(levelFilter) && (
                        <select className="form-select form-select-sm" style={{ width: 'auto' }} value={unitFilter} onChange={e => { setUnitFilter(e.target.value); setPage(1); }}>
                            <option value="">All units</option>
                            {UNIT_VALUES.map(u => <option key={u} value={u}>Unit {u}</option>)}
                        </select>
                    )}
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={subjectFilter}
                        onChange={e => { setSubjectFilter(e.target.value); setTopicFilter(''); setPage(1); }}
                    >
                        <option value="">Any subject</option>
                        {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={topicFilter}
                        onChange={e => { setTopicFilter(e.target.value); setPage(1); }}
                    >
                        <option value="">Any topic</option>
                        {topicOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={difficultyFilter}
                        onChange={e => { setDifficultyFilter(e.target.value); setPage(1); }}
                    >
                        <option value="">Any difficulty</option>
                        {QUESTIONDIFFICULTY.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <input
                        type="search"
                        className="form-control form-control-sm"
                        style={{ width: '240px' }}
                        placeholder="Search question / options / explanation…"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        title="Searches question text, options and explanation across ALL pages"
                    />
                    <button className="btn btn-sm btn-outline-success" onClick={fetchDrafts}>Refresh</button>
                </div>

                {selectedIds.size > 0 && (
                    <div className="alert alert-info py-2 d-flex align-items-center gap-2 mb-3">
                        <span className="me-auto">
                            {selectedIds.size} selected
                            {selectedDraftsWithMissing.length > 0 && (
                                <span className="badge bg-warning text-dark ms-2" title="Selected drafts that aren't ready for approval">
                                    ⚠ {selectedDraftsWithMissing.length} incomplete
                                </span>
                            )}
                        </span>
                        <button
                            className="btn btn-sm btn-success"
                            onClick={handleBulkApprove}
                            disabled={busy}
                            title={
                                selectedDraftsWithMissing.length > 0
                                    ? `${selectedDraftsWithMissing.length} of ${selectedIds.size} selected draft(s) are incomplete — you'll be warned before approving`
                                    : `Approve all ${selectedIds.size} selected draft(s)`
                            }
                        >Bulk Approve</button>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleBulkReject}
                            disabled={busy}
                        >Bulk Reject</button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowBulkEdit(true)}
                            disabled={busy}
                            title="Edit taxonomy fields on all selected drafts at once"
                        >Bulk Edit</button>
                        <button
                            className="btn btn-sm btn-link"
                            onClick={() => setSelectedIds(new Set())}
                        >Clear</button>
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: 30 }}>
                                    <input
                                        type="checkbox"
                                        checked={drafts.length > 0 && selectedIds.size === drafts.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th style={{ width: 50 }} title="Position within the current filter">#</th>
                                <th style={{ width: 70 }} title="Ingestion job this draft came from">Job</th>
                                <th>Question</th>
                                <th>Level</th>
                                <th>Subject</th>
                                <th>Topic</th>
                                <th title="Division (SSC/HSC)">Div</th>
                                <th title="Unit (VARSITY)">Unit</th>
                                <th title="Board (SSC/HSC)">Board</th>
                                <th title="BCS term (BCS level only)">BCS Term</th>
                                <th title="Exam year">Exam Year</th>
                                <th>Status</th>
                                <th title="Parsed question number from the source">Q#</th>
                                <th title="Duplicate of another draft in this job (same Q# and options)">Dup</th>
                                <th title="PDF-parsed answer">PDF ans</th>
                                <th title="AI-verified answer">AI ans</th>
                                <th title="Parser confidence for the PDF-parsed answer">PDF conf</th>
                                <th title="Claude's confidence in the AI answer">AI conf</th>
                                <th title="Correct option from the CSV — may be missing if the row had none">Answer</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {drafts.length === 0 ? (
                                <tr><td colSpan="21" className="text-center text-muted py-3">No drafts.</td></tr>
                            ) : drafts.map((d, i) => {
                                const missing = getMissingApprovalFields(d);
                                const incomplete = d.status === 'PENDING_REVIEW' && missing.length > 0;
                                return (
                                <tr key={d.draft_id} style={{ cursor: 'pointer', ...(incomplete ? { background: '#fef2f2', boxShadow: 'inset 3px 0 0 #ef4444' } : {}) }}>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(d.draft_id)}
                                            onChange={() => toggleOne(d.draft_id)}
                                        />
                                    </td>
                                    <td className="text-muted">{pageBaseSerial + i + 1}</td>
                                    <td onClick={() => setEditing(d)}>
                                        <span
                                            className="badge bg-light text-dark border"
                                            title="Ingestion job this draft came from"
                                        >
                                            #{d.job_id}
                                        </span>
                                        {d.source_uri && (
                                            <div
                                                className="small text-muted text-truncate"
                                                style={{ maxWidth: '150px' }}
                                                title={d.source_uri}
                                            >
                                                {baseName(d.source_uri)}
                                            </div>
                                        )}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {(d.group_key || '').trim() && (
                                            <span
                                                className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle me-1"
                                                title={`Passage question — group "${d.group_key}"${d.group_order != null ? `, order ${d.group_order}` : ''}`}
                                            >📘 Passage</span>
                                        )}
                                        {/* Claude flagged a figure/image this question needs but could not capture as
                                            text (an "[IMAGE NEEDED]" marker in the source, or a real embedded image in a
                                            PDF/image). Self-suppresses once any image URL is attached. */}
                                        {d.metadata?.missing_figure
                                            && !(d.question_image_url || d.explanation_image_url
                                                || ['option_a', 'option_b', 'option_c', 'option_d', 'option_e', 'option_f', 'option_g', 'option_h']
                                                    .some((k) => d[`${k}_image_url`])) && (
                                            <span
                                                className="badge bg-danger me-1"
                                                title="Claude detected an image/figure this question needs — attach the image before approving"
                                            >&lt;img needed&gt;</span>
                                        )}
                                        {/* Render the question the way it appears in the quiz (LaTeX via MathText),
                                            clamped to a few lines so the row stays compact (truncating the raw
                                            string would break math tokens). */}
                                        <div style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 480 }}>
                                            <MathText text={d.question_text} />
                                            {d.question_type === 'TEXT_COMPLETION' && (
                                                <span className="text-muted small ms-1" title="GRE Text Completion blanks">
                                                    ({blankCount(d)} blank{blankCount(d) === 1 ? '' : 's'})
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td onClick={() => setEditing(d)}>{d.level}</td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.subject || <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>{d.topic || <span className="text-muted">—</span>}</td>
                                    <td onClick={() => setEditing(d)}>{d.division || <span className="text-muted">—</span>}</td>
                                    <td onClick={() => setEditing(d)}>{d.unit || <span className="text-muted">—</span>}</td>
                                    <td onClick={() => setEditing(d)}>{d.board || <span className="text-muted">—</span>}</td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.level === 'BCS'
                                            ? (d.bcs_term || <span className="text-muted">—</span>)
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>{d.year || <span className="text-muted">—</span>}</td>
                                    <td onClick={() => setEditing(d)}>
                                        <span className={`badge ${STATUS_VARIANT[d.status] || 'bg-secondary'}`}>{d.status}</span>
                                        {incomplete && (
                                            <span
                                                className="badge bg-danger ms-1"
                                                title={`Missing before approve: ${missing.join(', ')} — click to fix`}
                                            >⚠ Incomplete</span>
                                        )}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.metadata?.source_number != null && String(d.metadata.source_number).trim() !== ''
                                            ? d.metadata.source_number
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {duplicateIds.has(d.draft_id)
                                            ? <span className="badge bg-warning text-dark" title="Duplicate of another draft in this job — same Q# and options">Dup</span>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.metadata?.pdf_answer
                                            ? <span className="badge bg-dark">{d.metadata.pdf_answer}</span>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.metadata?.ai_answer?.correct_option
                                            ? <span className="badge bg-info text-dark">{d.metadata.ai_answer.correct_option}</span>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.metadata?.pdf_confidence != null
                                            ? Number(d.metadata.pdf_confidence).toFixed(2)
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {d.metadata?.ai_answer?.confidence != null
                                            ? Number(d.metadata.ai_answer.confidence).toFixed(2)
                                            : <span className="text-muted">—</span>}
                                    </td>
                                    <td onClick={() => setEditing(d)}>
                                        {(d.text_answer || '').trim()
                                            ? <span className="badge bg-dark" title={d.text_answer}>text ans</span>
                                            : d.question_type === 'TEXT'
                                                ? <span className="badge bg-secondary" title="Unscored (e.g. Writing/Speaking)">no key</span>
                                                : d.question_type === 'TEXT_COMPLETION'
                                                    ? (blanksAllAnswered(d)
                                                        ? <span className="badge bg-dark" title="Correct choice per blank">{blanksAnswerLabel(d)}</span>
                                                        : <span className="badge bg-warning text-dark" title="One or more blanks have no correct choice — set before approve">⚠ missing</span>)
                                                    : d.correct_option
                                                        ? <span className="badge bg-light text-dark">{d.correct_option}</span>
                                                        : <span className="badge bg-warning text-dark" title="No answer key — set before approve">⚠ missing</span>}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={(e) => { e.stopPropagation(); setEditing(d); }}
                                        >Review</button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        Page {page} of {totalPages} — {total} total
                        {(() => {
                            const n = drafts.filter(d => d.status === 'PENDING_REVIEW' && getMissingApprovalFields(d).length > 0).length;
                            return n > 0 ? <span className="badge bg-danger ms-2" title="Rows highlighted red — open each to complete the missing fields">⚠ {n} incomplete on this page</span> : null;
                        })()}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        >
                            {itemsPerPageOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
                            <option value={ALL_PAGE_SIZE}>All</option>
                        </select>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1}
                        >Prev</button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                        >Next</button>
                    </div>
                </div>
            </div>

            {editing && (
                <ParsedDraftEditor
                    draft={editing}
                    onClose={() => setEditing(null)}
                    onApproved={(draftId, questionId, updatedDraft) => {
                        // Merge the saved edits into the row (so reopening shows the current
                        // content, e.g. an attached image) and flip its status badge to APPROVED.
                        setDrafts(prev => prev.map(d => d.draft_id === draftId ? { ...d, ...(updatedDraft || {}), status: 'APPROVED' } : d));
                        // Keep the open editor's source object in sync so it doesn't re-seed to stale data.
                        setEditing(prev => (prev && prev.draft_id === draftId ? { ...prev, ...(updatedDraft || {}), status: 'APPROVED' } : prev));
                        onApproved && onApproved(draftId, questionId);
                    }}
                    onRejected={(draftId) => {
                        // Rejected drafts are hidden from this grid — drop the row and close the editor.
                        setDrafts(prev => prev.filter(d => d.draft_id !== draftId));
                        setTotal(prev => Math.max(0, prev - 1));
                        setEditing(prev => (prev && prev.draft_id === draftId ? null : prev));
                    }}
                    onNavigate={navigateEditing}
                    hasPrev={editingIndex > 0}
                    hasNext={editingIndex >= 0 && editingIndex < drafts.length - 1}
                    serial={editingIndex >= 0 ? pageBaseSerial + editingIndex + 1 : null}
                    previewOpen={previewOpen}
                    setPreviewOpen={setPreviewOpen}
                    sourceDoc={sourceDoc}
                    setSourceDoc={setSourceDoc}
                />
            )}

            <BulkEditModal
                open={showBulkEdit}
                count={selectedIds.size}
                onClose={() => setShowBulkEdit(false)}
                onApply={handleBulkEdit}
            />
        </div>
    );
};

export default ParsedDraftsGrid;
